import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { buildCatalog, createCatalogTranslator, mergeCatalogs } from "./catalogs.js";
import { ensureNamespaces, EAGER_NAMESPACES } from "./catalogLoader.js";
import { getManifestI18n, isLocaleSupportedByManifest } from "./manifestI18n.js";
import {
  FALLBACK_LOCALE,
  localeToLanguage,
  normalizeLocale,
  readLocaleFromStorage,
  resolveLocale,
  writeLocaleToStorage,
} from "./localeResolution.js";

const LocaleContext = createContext(null);

export function LocaleProvider({
  surface = "platform",
  initialLocale,
  urlLocale = null,
  manifest = null,
  accountPreferences = null,
  preloadNamespaces = [],
  children,
}) {
  const manifestI18n = useMemo(
    () => (surface === "clientView" ? getManifestI18n(manifest) : null),
    [surface, manifest]
  );

  const [locale, setLocaleState] = useState(() =>
    normalizeLocale(
      initialLocale ??
        resolveLocale({
          surface,
          urlLocale,
          storedLocale: readLocaleFromStorage(),
          manifest,
          accountPreferences,
        })
    )
  );

  const [catalog, setCatalog] = useState(() => buildCatalog(locale));

  useEffect(() => {
    if (surface === "clientView" && !manifest) {
      return;
    }
    const resolved = resolveLocale({
      surface,
      urlLocale,
      storedLocale: readLocaleFromStorage(),
      manifest,
      accountPreferences,
    });
    const normalized = normalizeLocale(resolved);
    setLocaleState(normalized);
    if (surface === "clientView") {
      writeLocaleToStorage(normalized);
    }
  }, [surface, urlLocale, manifest, accountPreferences]);

  useEffect(() => {
    setCatalog(buildCatalog(locale));
  }, [locale]);

  useEffect(() => {
    if (!preloadNamespaces.length) {
      return undefined;
    }
    let cancelled = false;
    ensureNamespaces(locale, preloadNamespaces).then((patch) => {
      if (!cancelled && Object.keys(patch).length) {
        setCatalog((current) => mergeCatalogs(current, patch));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locale, preloadNamespaces]);

  const language = useMemo(() => localeToLanguage(locale), [locale]);
  const supportedLocales = useMemo(
    () => manifestI18n?.supportedLocales || ["es-CO", "en-US"],
    [manifestI18n]
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLocale = useCallback(
    (nextLocale) => {
      const normalized = normalizeLocale(nextLocale);
      if (manifestI18n && !isLocaleSupportedByManifest(normalized, manifestI18n)) {
        return;
      }
      setLocaleState(normalized);
      writeLocaleToStorage(normalized);
    },
    [manifestI18n]
  );

  const fallbackCatalog = useMemo(() => buildCatalog(FALLBACK_LOCALE), []);

  const translation = useMemo(
    () => createCatalogTranslator(locale, catalog),
    [locale, catalog]
  );

  const t = useCallback((key, params) => translation.t(key, params), [translation]);

  const exists = useCallback((key) => translation.exists(key), [translation]);

  const ensureNamespace = useCallback(
    async (namespace) => {
      const patch = await ensureNamespaces(locale, [namespace]);
      if (patch[namespace]) {
        setCatalog((current) => mergeCatalogs(current, patch));
      }
      return Boolean(patch[namespace]);
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      language,
      supportedLocales,
      setLocale,
      t,
      exists,
      ensureNamespace,
      translation,
      loadedNamespaces: Object.keys(catalog),
      eagerNamespaces: EAGER_NAMESPACES,
    }),
    [locale, language, supportedLocales, setLocale, t, exists, ensureNamespace, translation, catalog]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}

export function useOptionalLocale() {
  return useContext(LocaleContext);
}
